import { StrictMode, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import App from "./App";
import { useAppStore } from "./stores/app.store";
import { createTrpcClient, isClientError, trpc } from "./lib/trpc";
import "@fontsource-variable/montserrat"; // Logo-Schriftart, lokal gebündelt (offline-tauglich)
import "@mavatech/theme/base.css";
import "./index.css";

function Root() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        // Fehler beim Speichern immer anzeigen — auch an Stellen, die keine eigene Fehlerbehandlung haben
        // (z. B. Adress-/Ansprechpartner-Dialoge). Sonst wirkt "Speichern" einfach wie ohne Reaktion.
        mutationCache: new MutationCache({
          onError: (
            err: unknown,
            _vars: unknown,
            _ctx: unknown,
            mutation: { options: { onError?: unknown } },
          ) => {
            if (mutation.options.onError) return;
            useAppStore.getState().addToast({
              type: "error",
              title: "Speichern nicht möglich",
              description:
                err instanceof Error ? err.message : "Unbekannter Fehler",
            });
          },
        }),
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            staleTime: 30_000,
            // Browser melden "offline" gelegentlich fälschlich (WLAN-Wechsel, Ruhezustand). Mit dem
            // Standard "online" pausiert react-query dann alle Abfragen — die Seite hängt auf "Lädt …",
            // bis man neu lädt (Cmd+R). Daher immer versuchen und bei Netzfehlern neu probieren.
            networkMode: "always",
            refetchOnReconnect: true,
            retry: (count, err) => count < 3 && !isClientError(err),
          },
          mutations: { networkMode: "always" },
        },
      }),
  );
  const [trpcClient] = useState(() => createTrpcClient());

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </trpc.Provider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);

// Zahlenfelder: beim Anklicken/Anspringen den ganzen Inhalt markieren, damit die Eingabe ihn gleich
// überschreibt (sonst bleiben vorbelegte Nullen stehen). Gilt app-weit für type="number" und
// Betragsfelder (inputMode decimal/numeric).
{
  const isNumeric = (el: EventTarget | null): el is HTMLInputElement =>
    el instanceof HTMLInputElement &&
    (el.type === "number" ||
      el.inputMode === "decimal" ||
      el.inputMode === "numeric");
  let justFocused: HTMLInputElement | null = null;
  document.addEventListener("focusin", (e) => {
    if (!isNumeric(e.target)) return;
    justFocused = e.target;
    e.target.select();
  });
  // Der Klick setzt sonst nach dem Fokus den Cursor und hebt die Markierung wieder auf.
  document.addEventListener(
    "mouseup",
    (e) => {
      if (justFocused && e.target === justFocused) e.preventDefault();
      justFocused = null;
    },
    true,
  );
}

// Nur registrieren, damit der Browser die App als "installierbar" (Zum Startbildschirm
// hinzufügen) erkennt — der Service Worker selbst cached nichts, siehe public/sw.js.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}
